import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';

import { call, restProps } from '../utils';

import Tab from './Tab';
import styles from './Panel.less';

const cxb = cx.bind(styles);

const getSelectedIndexFromProps = ({ children }) => React.Children.toArray(children).findIndex((x) => x.props.selected);
const hasSelectedChild = (props) => ~getSelectedIndexFromProps(props);

/**
 * **Access:**
 * ```js
 * import {Tabs} from '@iss/client-react-components'
 *
 * <Tabs.Panel>
 * 	<Tabs.Item .../>
 * 	<Tabs.Item .../>
 * </Tabs.Panel>
 *
 * ```
 *
 * Represents a tab panel. The Panel will act as a switch, only rendering the content of the active item.
 *
 * @class Tabs.Panel
 * @visibleName Panel
 * @component
 */
export default class TabPanel extends React.Component {
  static propTypes = {
    /**
     * Only use Tabs.Item elements as children.
     */
    children: PropTypes.any.isRequired,

    /**
     * Specifies the layout of the tab panel. Tabs on top, or side.
     */
    layout: PropTypes.oneOf(['horizontal', 'vertical']).isRequired,

    /**
     * for controlled state, you can specify the index of the tab to be active. Alternatively, you can set
     * the `selected` prop on the items directly.
     */
    selectedIndex: PropTypes.number,

    /**
     * Callback function when the active tab is changing. (Required if controlling active tab through props)
     */
    onChange: PropTypes.func,

    /**
     * Use small style.
     *
     * **Note:** subText on tabs will not render on small mode.
     */
    small: PropTypes.bool,
  };

  static defaultProps = {
    layout: 'horizontal',
  };

  state = {
    activeChild: 0,
  };

  getActiveIndex() {
    const {
      props: { selectedIndex },
      state: { activeChild },
    } = this;

    const selected = hasSelectedChild(this.props) ? getSelectedIndexFromProps(this.props) : activeChild;
    const index = (selectedIndex == null ? selected : selectedIndex) || 0;

    return Math.max(index, 0);
  }

  handleTabActivation(index) {
    const {
      props: { selectedIndex, onChange },
    } = this;

    if (onChange || selectedIndex != null || hasSelectedChild(this.props)) {
      return call(onChange, index);
    }

    this.setState({ activeChild: index });
  }

  render() {
    const { className, children, layout, small, ...props } = this.props;

    const tabs = React.Children.toArray(children);
    const activeIndex = this.getActiveIndex();

    return (
      <div className={cxb('panel', layout, { small }, className)} {...restProps(TabPanel, props)}>
        <div className={cxb('tab-bar')}>
          {tabs.map(({ props: { badge, label, subText } }, i) => (
            <Tab
              small={small}
              className={cxb('tab', { active: i === activeIndex })}
              active={i === activeIndex}
              label={label}
              subText={subText}
              badge={badge}
              key={i}
              onClick={() => this.handleTabActivation(i)}
            />
          ))}
        </div>
        <div className={cxb('content')}>{tabs[activeIndex]}</div>
      </div>
    );
  }
}
